import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { api, slugify, titleize, primaryImage, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ImagePlus, Star, Trash2, Upload, CheckCircle2 } from "lucide-react";

const VENUE_TYPES = [
    "bars","restaurants","pubs","hotels","function-rooms","private-dining-rooms",
    "rooftop-venues","event-spaces","meeting-rooms","conference-venues","wedding-venues",
    "studios","galleries","warehouses","halls","clubs","museums","gardens",
    "boats","mansions","lofts","coworking-spaces","unusual-venues",
];
const EVENT_TYPES = [
    "birthday-parties","private-parties","corporate-events","meetings","weddings",
    "private-dining","christmas-parties","engagement-parties","baby-showers",
    "product-launches","networking-events","conferences","workshops","training-days",
    "filming","photoshoots","pop-up-events","charity-events"
];
const AMENITIES = ["WiFi","Bar","Kitchen","Catering","AV Equipment","Projector","Microphone","Stage","Dance Floor","Outdoor Space","Parking","Disabled Access","Air Conditioning","Natural Light","Private Entrance","Toilets","Cloakroom","Security","Late Licence","Pet Friendly","Wheelchair Accessible"];

const STEPS = ["Business", "Venue details", "Photos", "Features", "Preview"];

const emptyVenue = {
    name: "", venue_type: "event-spaces", country: "United Kingdom", city: "", area: "",
    address: "", postcode: "", short_description: "", long_description: "",
    capacity_seated: "", capacity_standing: "", price_from: "", price_currency: "GBP",
    pricing_model: "minimum_spend", contact_email: "", contact_phone: "", website: "",
    images: [], amenities: [], event_types: [],
};

export default function OwnerSignup() {
    const nav = useNavigate();
    const loc = useLocation();
    const { user, refresh } = useAuth();
    const editing = loc.state?.editing || null;
    const [step, setStep] = useState(editing ? 1 : 0);
    const [biz, setBiz] = useState({
        business_name: user?.business_name || "", phone: user?.phone || "", country: user?.country || "United Kingdom",
    });
    const [venue, setVenue] = useState(editing ? {
        ...emptyVenue, ...editing,
        capacity_seated: editing.capacity_seated || "", capacity_standing: editing.capacity_standing || "",
        price_from: editing.price_from || "",
    } : emptyVenue);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user) {
            // Redirect unauth
            const t = setTimeout(() => nav("/list-your-venue"), 400);
            return () => clearTimeout(t);
        }
    }, [user, nav]);

    const setV = (patch) => setVenue((v) => ({ ...v, ...patch }));

    const toggleArr = (key, val) => {
        setVenue((v) => ({
            ...v,
            [key]: v[key].includes(val) ? v[key].filter((x) => x !== val) : [...v[key], val],
        }));
    };

    const canNext = () => {
        if (step === 0) return biz.business_name && biz.phone;
        if (step === 1) return venue.name && venue.venue_type && venue.city && venue.short_description;
        if (step === 2) return true;
        if (step === 3) return venue.amenities.length >= 1 && venue.event_types.length >= 1;
        return true;
    };

    const submitBiz = async () => {
        await api.post("/auth/become-owner", biz);
        await refresh();
        setStep(1);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                ...venue,
                capacity_seated: venue.capacity_seated ? Number(venue.capacity_seated) : undefined,
                capacity_standing: venue.capacity_standing ? Number(venue.capacity_standing) : undefined,
                price_from: venue.price_from ? Number(venue.price_from) : undefined,
                status: "pending",
            };
            if (editing) {
                await api.put(`/venues/${editing.venue_id}`, payload);
                toast.success("Venue updated");
            } else {
                await api.post("/venues", payload);
                toast.success("Venue submitted for review!");
            }
            nav("/dashboard/venues");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) return null;

    return (
        <div className="container-app py-8 md:py-12 max-w-4xl">
            <div className="mb-8">
                <p className="text-xs uppercase tracking-widest text-secondary font-semibold">{editing ? "Edit your venue" : "Add your venue"}</p>
                <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{editing ? venue.name : "Let's get you set up"}</h1>
                <div className="mt-5 flex items-center gap-3">
                    <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
                    <span className="text-sm text-muted-foreground shrink-0">{step + 1} / {STEPS.length}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {STEPS.map((s, i) => (
                        <span key={s} className={`px-2 py-1 rounded-full ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`}>{s}</span>
                    ))}
                </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
                {step === 0 && (
                    <div className="space-y-4" data-testid="step-business">
                        <h2 className="font-display text-2xl font-semibold">Your business</h2>
                        <div><Label>Business name</Label><Input value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} data-testid="biz-name" /></div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div><Label>Phone</Label><Input value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} data-testid="biz-phone" /></div>
                            <div><Label>Country</Label><Input value={biz.country} onChange={(e) => setBiz({ ...biz, country: e.target.value })} data-testid="biz-country" /></div>
                        </div>
                        <div className="text-xs text-muted-foreground">Signed in as {user.email}</div>
                    </div>
                )}

                {step === 1 && (
                    <div className="space-y-4" data-testid="step-details">
                        <h2 className="font-display text-2xl font-semibold">Venue details</h2>
                        <div><Label>Venue name</Label><Input value={venue.name} onChange={(e) => setV({ name: e.target.value })} data-testid="venue-name" /></div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label>Venue type</Label>
                                <Select value={venue.venue_type} onValueChange={(v) => setV({ venue_type: v })}>
                                    <SelectTrigger data-testid="venue-type"><SelectValue /></SelectTrigger>
                                    <SelectContent className="max-h-80">
                                        {VENUE_TYPES.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Pricing model</Label>
                                <Select value={venue.pricing_model} onValueChange={(v) => setV({ pricing_model: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="hourly">Hourly hire</SelectItem>
                                        <SelectItem value="day">Day hire</SelectItem>
                                        <SelectItem value="minimum_spend">Minimum spend</SelectItem>
                                        <SelectItem value="per_person">Per person</SelectItem>
                                        <SelectItem value="package">Package</SelectItem>
                                        <SelectItem value="price_on_enquiry">Price on enquiry</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div><Label>Country</Label><Input value={venue.country} onChange={(e) => setV({ country: e.target.value })} /></div>
                            <div><Label>City</Label><Input value={venue.city} onChange={(e) => setV({ city: e.target.value })} data-testid="venue-city" /></div>
                            <div><Label>Area / neighbourhood</Label><Input value={venue.area} onChange={(e) => setV({ area: e.target.value })} /></div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div><Label>Address</Label><Input value={venue.address} onChange={(e) => setV({ address: e.target.value })} /></div>
                            <div><Label>Postcode</Label><Input value={venue.postcode} onChange={(e) => setV({ postcode: e.target.value })} /></div>
                        </div>
                        <div><Label>Short description</Label><Input value={venue.short_description} onChange={(e) => setV({ short_description: e.target.value })} placeholder="A one-liner about your venue" data-testid="venue-short-desc" /></div>
                        <div><Label>Long description</Label><Textarea rows={5} value={venue.long_description} onChange={(e) => setV({ long_description: e.target.value })} placeholder="Tell guests what makes your venue special." /></div>
                        <div className="grid md:grid-cols-4 gap-4">
                            <div><Label>Seated capacity</Label><Input type="number" min="1" value={venue.capacity_seated} onChange={(e) => setV({ capacity_seated: e.target.value })} /></div>
                            <div><Label>Standing capacity</Label><Input type="number" min="1" value={venue.capacity_standing} onChange={(e) => setV({ capacity_standing: e.target.value })} /></div>
                            <div><Label>Price from</Label><Input type="number" min="0" value={venue.price_from} onChange={(e) => setV({ price_from: e.target.value })} data-testid="venue-price" /></div>
                            <div>
                                <Label>Currency</Label>
                                <Select value={venue.price_currency} onValueChange={(v) => setV({ price_currency: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["GBP","USD","EUR","AUD","CAD","AED","SGD","NZD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div><Label>Contact email</Label><Input type="email" value={venue.contact_email} onChange={(e) => setV({ contact_email: e.target.value })} data-testid="venue-contact-email" /></div>
                            <div><Label>Contact phone</Label><Input value={venue.contact_phone} onChange={(e) => setV({ contact_phone: e.target.value })} /></div>
                            <div><Label>Website</Label><Input value={venue.website} onChange={(e) => setV({ website: e.target.value })} /></div>
                        </div>
                    </div>
                )}

                {step === 2 && <PhotosStep venue={venue} setVenue={setVenue} />}

                {step === 3 && (
                    <div className="space-y-6" data-testid="step-features">
                        <div>
                            <h2 className="font-display text-2xl font-semibold">Amenities</h2>
                            <p className="text-sm text-muted-foreground mb-4">Pick everything your venue offers.</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {AMENITIES.map((a) => (
                                    <label key={a} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${venue.amenities.includes(a) ? "border-primary bg-accent/40" : "border-border hover:bg-muted/50"}`}>
                                        <Checkbox checked={venue.amenities.includes(a)} onCheckedChange={() => toggleArr("amenities", a)} />
                                        {a}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h2 className="font-display text-2xl font-semibold">Event types</h2>
                            <p className="text-sm text-muted-foreground mb-4">Which events is your venue perfect for?</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {EVENT_TYPES.map((e) => (
                                    <label key={e} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${venue.event_types.includes(e) ? "border-primary bg-accent/40" : "border-border hover:bg-muted/50"}`}>
                                        <Checkbox checked={venue.event_types.includes(e)} onCheckedChange={() => toggleArr("event_types", e)} />
                                        {titleize(e)}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div data-testid="step-preview">
                        <h2 className="font-display text-2xl font-semibold mb-4">Preview & submit</h2>
                        <div className="rounded-xl border border-border overflow-hidden">
                            <div className="aspect-[3/1] bg-muted">
                                {venue.images[0] && <img src={resolveImageUrl(venue.images[0].url)} alt="" className="h-full w-full object-cover" />}
                            </div>
                            <div className="p-5">
                                <h3 className="font-display text-2xl font-semibold">{venue.name || "Your venue name"}</h3>
                                <p className="text-muted-foreground text-sm mt-1">{venue.city}{venue.area ? `, ${venue.area}` : ""}</p>
                                <p className="mt-3">{venue.short_description}</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {venue.amenities.slice(0, 8).map((a) => <span key={a} className="text-xs bg-muted rounded-full px-3 py-1">{a}</span>)}
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-4">
                            When you submit, your listing status becomes <b>pending review</b>. Our team reviews new listings within 1-2 business days.
                        </p>
                    </div>
                )}

                <div className="mt-8 flex justify-between">
                    <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="step-back">
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    {step === 0 ? (
                        <Button onClick={submitBiz} disabled={!canNext()} className="rounded-full bg-primary text-primary-foreground" data-testid="step-next">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
                    ) : step < STEPS.length - 1 ? (
                        <Button onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()} className="rounded-full bg-primary text-primary-foreground" data-testid="step-next">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
                    ) : (
                        <Button onClick={submit} disabled={submitting} className="rounded-full bg-primary text-primary-foreground" data-testid="submit-venue">
                            {submitting ? "Submitting…" : (editing ? "Save changes" : "Submit for review")}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function PhotosStep({ venue, setVenue }) {
    const fileRef = useRef(null);
    const [urlInput, setUrlInput] = useState("");
    const [uploading, setUploading] = useState(false);

    const addUrl = () => {
        if (!urlInput.trim()) return;
        setVenue((v) => ({
            ...v,
            images: [...v.images, { url: urlInput.trim(), is_primary: v.images.length === 0, order: v.images.length }],
        }));
        setUrlInput("");
    };

    const upload = async (files) => {
        setUploading(true);
        try {
            for (const file of Array.from(files)) {
                const fd = new FormData(); fd.append("file", file);
                const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                // Store the relative URL (e.g. /api/files/...). resolveImageUrl() prefixes BACKEND_URL at render time.
                setVenue((v) => ({
                    ...v, images: [...v.images, { url: data.url, is_primary: v.images.length === 0, order: v.images.length }],
                }));
            }
        } catch (e) {
            toast.error("Upload failed");
        } finally { setUploading(false); }
    };

    const setPrimary = (idx) => {
        setVenue((v) => ({ ...v, images: v.images.map((img, i) => ({ ...img, is_primary: i === idx })) }));
    };
    const remove = (idx) => {
        setVenue((v) => ({ ...v, images: v.images.filter((_, i) => i !== idx) }));
    };

    return (
        <div data-testid="step-photos">
            <h2 className="font-display text-2xl font-semibold">Photos</h2>
            <p className="text-sm text-muted-foreground mb-4">Add at least 3 great photos. Choose one as your main image.</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {venue.images.map((img, i) => (
                    <div key={i} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-border">
                        <img src={resolveImageUrl(img.url)} alt="" className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setPrimary(i)} className="gap-1">
                                <Star className={`h-4 w-4 ${img.is_primary ? "fill-yellow-400 text-yellow-400" : ""}`} />
                                {img.is_primary ? "Main" : "Set as main"}
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                        {img.is_primary && (
                            <div className="absolute top-2 left-2 rounded-full bg-secondary text-secondary-foreground text-[10px] font-semibold px-2 py-0.5">MAIN</div>
                        )}
                    </div>
                ))}
                <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground text-sm cursor-pointer hover:border-primary hover:text-primary transition-colors">
                    <input type="file" accept="image/*" multiple className="hidden" ref={fileRef} onChange={(e) => upload(e.target.files)} data-testid="upload-file-input" />
                    <ImagePlus className="h-6 w-6" />
                    {uploading ? "Uploading…" : "Upload"}
                </label>
            </div>

            <div className="mt-6 rounded-xl border border-border p-4 bg-muted/30">
                <Label>Or add by image URL</Label>
                <div className="flex gap-2 mt-1">
                    <Input placeholder="https://…" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} data-testid="image-url-input" />
                    <Button variant="outline" onClick={addUrl} data-testid="add-image-url">Add</Button>
                </div>
            </div>
        </div>
    );
}
